// LatestEvents.js - Updated version
import React from "react";
import {
  Box,
  Typography,
  Chip,
  Avatar,
  Stack,
  Paper,
  alpha,
  useTheme,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Whatshot as FireIcon,
  Cloud as SmokeIcon,
  Event as EventIcon,
  AccessTime as TimeIcon,
  CameraAlt as CameraIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  Public as PublicIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";

const MotionPaper = motion(Paper);

function LatestEvents(props) {
  const theme = useTheme();
  const { data, verified = false } = props;

  if (!data) return null;

  const {
    species = [],
    thumbnail,
    camera_name,
    date,
    sms_sent = false,
    nasa_tag = false,
    file,
  } = data;

  // Process camera name
  let cameraDisplayName = camera_name || "Unknown Camera";
  if (cameraDisplayName && cameraDisplayName.includes("PTZ")) {
    const nameParts = cameraDisplayName.split("-");
    cameraDisplayName = nameParts[1] || cameraDisplayName;
  }

  // Parse date string in format "DD-MM-YYYY HH:MM:SS"
  const parseDate = (dateString) => {
    if (!dateString) return null;
    try {
      const parts = dateString.split(" ");
      const dateParts = parts[0].split("-");
      const timeParts = parts[1] ? parts[1].split(":") : [0, 0, 0];

      const year = parseInt(dateParts[2]);
      const month = parseInt(dateParts[1]) - 1;
      const day = parseInt(dateParts[0]);
      const hour = parseInt(timeParts[0]);
      const minute = parseInt(timeParts[1]);
      const second = parseInt(timeParts[2]);

      return new Date(year, month, day, hour, minute, second);
    } catch (error) {
      console.error("Date parsing error:", error);
      return null;
    }
  };

  // Format date for display (original format)
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "Unknown";
    return dateString;
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    const dateObj = parseDate(dateString);
    if (!dateObj) return dateString || "Unknown";

    const now = new Date();
    const diffMs = now - dateObj;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    return formatDisplayDate(dateString);
  };

  // Get event type based on species
  const getEventType = () => {
    if (!species || species.length === 0) {
      return {
        type: "ping",
        color: theme.palette.info.main,
        icon: <EventIcon />,
        label: "Ping",
        bgColor: alpha(theme.palette.info.main, 0.2),
      };
    }
    const hasFire = species.some(
      (s) => s.id === "fire" || s.name?.toLowerCase() === "fire",
    );
    const hasSmoke = species.some(
      (s) => s.id === "smoke" || s.name?.toLowerCase() === "smoke",
    );
    if (hasFire && hasSmoke) {
      return {
        type: "fire+smoke",
        color: "#ff4444",
        icon: <FireIcon />,
        label: "FIRE+SMOKE",
        bgColor: alpha("#ff4444", 0.2),
      };
    }
    if (hasFire) {
      return {
        type: "fire",
        color: "#dc3545",
        icon: <FireIcon />,
        label: "FIRE",
        bgColor: alpha("#dc3545", 0.2),
      };
    }
    if (hasSmoke) {
      return {
        type: "smoke",
        color: "#62605f",
        icon: <SmokeIcon />,
        label: "SMOKE",
        bgColor: alpha("#fd7e14", 0.2),
      };
    }
    return {
      type: "ping",
      color: theme.palette.info.main,
      icon: <EventIcon />,
      label: "Ping",
      bgColor: alpha(theme.palette.info.main, 0.2),
    };
  };

  const eventType = getEventType();

  // Fix thumbnail URL
  const thumbnailUrl = thumbnail?.replace(
    "http://127.0.0.1:8000",
    "https://api.forestwatch.org.pk",
  );

  // Fix GIF URL
  const gifUrl = file?.replace(
    "http://127.0.0.1:8000",
    "https://api.forestwatch.org.pk",
  );

  const displayDate = formatDisplayDate(date);
  const relativeTime = formatRelativeTime(date);

  return (
    <MotionPaper
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      elevation={0}
      sx={{
        width: "100%",
        p: 1.5,
        borderRadius: 2,
        bgcolor: "rgba(255,255,255,0.05)",
        backdropFilter: "blur(10px)",
        border: `1px solid ${alpha(eventType.color, 0.3)}`,
        transition: "all 0.3s ease",
        cursor: "pointer",
        "&:hover": {
          bgcolor: "rgba(255,255,255,0.1)",
          transform: "translateY(-2px)",
          boxShadow: `0 8px 25px ${alpha(eventType.color, 0.15)}`,
        },
      }}
    >
      <Stack direction="row" spacing={1.5}>
        {/* Thumbnail */}
        <Box sx={{ position: "relative" }}>
          <Avatar
            variant="rounded"
            src={thumbnailUrl}
            sx={{
              width: 80,
              height: 80,
              borderRadius: 2,
              bgcolor: eventType.bgColor,
              border: `2px solid ${alpha(eventType.color, 0.5)}`,
            }}
          >
            {!thumbnailUrl && eventType.icon}
          </Avatar>

          {/* Event Type Badge */}
          <Chip
            label={eventType.label}
            size="small"
            icon={eventType.icon}
            sx={{
              position: "absolute",
              top: -8,
              left: -8,
              height: 22,
              fontSize: "0.65rem",
              fontWeight: 700,
              bgcolor: eventType.color,
              color: "#fff",
              "& .MuiChip-icon": {
                color: "#fff",
                fontSize: 12,
                margin: 0,
              },
            }}
          />

          {/* Verified by FIRMS Badge - top right of thumbnail */}
          {verified && (
            <Chip
              icon={<VerifiedIcon sx={{ fontSize: 10 }} />}
              label="FIRMS"
              size="small"
              sx={{
                position: "absolute",
                top: -8,
                right: -8,
                height: 18,
                fontSize: "0.55rem",
                fontWeight: 700,
                bgcolor: "#4caf50",
                color: "#fff",
                "& .MuiChip-icon": {
                  color: "#fff",
                  fontSize: 10,
                },
              }}
            />
          )}
        </Box>

        {/* Event Details */}
        <Box sx={{ flex: 1 }}>
          {/* Camera Name */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ mb: 0.5 }}
          >
            <CameraIcon sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }} />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#fff",
                fontSize: "0.85rem",
              }}
            >
              {cameraDisplayName}
            </Typography>
          </Stack>

          {/* Date - Show relative time */}
          <Tooltip title={`Full date: ${displayDate}`} arrow>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ mb: 0.5 }}
            >
              <TimeIcon sx={{ fontSize: 10, color: "rgba(255,255,255,0.5)" }} />
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.6)" }}
              >
                {relativeTime}
              </Typography>
            </Stack>
          </Tooltip>

          {/* Species Tags */}
          {species && species.length > 0 && (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ flexWrap: "wrap", gap: 0.5, mt: 0.5 }}
            >
              {species.slice(0, 3).map((specie, idx) => (
                <Chip
                  key={idx}
                  label={specie.name || specie.id}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.6rem",
                    fontWeight: 500,
                    bgcolor: alpha(specie.color || "#fff", 0.15),
                    color: specie.color || "#fff",
                  }}
                />
              ))}
              {species.length > 3 && (
                <Chip
                  label={`+${species.length - 3}`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.6rem",
                    bgcolor: "rgba(255,255,255,0.1)",
                    color: "rgba(255,255,255,0.7)",
                  }}
                />
              )}
            </Stack>
          )}

          {/* Status Indicators */}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {sms_sent && (
              <Tooltip title="SMS Notification Sent" arrow>
                <CheckCircleIcon sx={{ fontSize: 14, color: "#4caf50" }} />
              </Tooltip>
            )}
            {nasa_tag && (
              <Tooltip title="NASA Fire Detection" arrow>
                <PublicIcon sx={{ fontSize: 14, color: "#2196f3" }} />
              </Tooltip>
            )}
          </Stack>
        </Box>

        {/* View Button */}
        {gifUrl && (
          <Tooltip title="View Event GIF" arrow>
            <IconButton
              component="a"
              href={gifUrl}
              target="_blank"
              size="small"
              sx={{
                alignSelf: "center",
                color: "rgba(255,255,255,0.5)",
                "&:hover": {
                  color: eventType.color,
                  bgcolor: alpha(eventType.color, 0.1),
                },
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </MotionPaper>
  );
}

export default LatestEvents;
